import React from "react";
import { Link } from "react-router-dom";
import { EnterpriseNavbar } from "../components/enterprise/EnterpriseNavbar";
import { Footer } from "../components/footer";
import useDocumentMeta from "../hooks/useDocumentMeta";
import EN from "../data/en.json";
import AR from "../data/ar.json";
import {
  TUTORIAL_CONTENT,
  TUTORIAL_INDEX,
} from "../data/tutorialContent";

/**
 * /guide — the FarmTopia platform usage guide index. Mirrors /crops: a hero
 * plus a card grid of the 15 walkthrough sections, each linking to
 * /guide/{slug}. Rendered inside the enterprise shell (EnterpriseNavbar +
 * Footer).
 */
export default function Guide({ lang = "EN", toggleLanguage }) {
  const isArabic = lang === "AR";
  const langKey = isArabic ? "ar" : "en";
  const idx = TUTORIAL_INDEX[langKey];

  const data = isArabic ? AR : EN;
  const navActions = data.Enterprise?.navCtas || { primary: "", secondary: "" };
  const navItems = [
    { label: isArabic ? "نظرة عامة" : "Overview", url: "/enterprises" },
    { label: isArabic ? "المحاصيل" : "Crops", url: "/crops" },
    { label: isArabic ? "الدليل" : "Guide", url: "/guide" },
    { label: isArabic ? "المنصة" : "Platform", url: "/platform" },
  ];

  useDocumentMeta({
    title: `${idx.title} — FarmTopia`,
    description: idx.description,
  });

  return (
    <div
      dir={isArabic ? "rtl" : "ltr"}
      className="min-h-screen bg-gray-50 text-slate-900"
    >
      <EnterpriseNavbar
        lang={lang}
        toggleLanguage={toggleLanguage}
        navItems={navItems}
        actions={navActions}
      />

      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Hero */}
        <header className={isArabic ? "text-right" : "text-left"}>
          <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-emerald-700">
            {idx.eyebrow}
          </p>
          <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl">
            {idx.title}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-gray-600">
            {idx.description}
          </p>
        </header>

        {/* Card grid */}
        <ol className="mt-10 grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {TUTORIAL_CONTENT.map((section, i) => {
            const c = section[langKey];
            return (
              <li key={section.slug}>
                <Link
                  to={`/guide/${section.slug}`}
                  dir={isArabic ? "rtl" : "ltr"}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white no-underline shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >
                  <div className="relative h-44 w-full overflow-hidden bg-gray-100">
                    <img
                      src={section.image}
                      alt={c.imageAlt || c.title}
                      loading="lazy"
                      className="h-full w-full object-cover object-top transition group-hover:scale-105"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-6">
                    <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                      {`${idx.stepLabel} ${i + 1}`}
                    </p>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {c.title}
                    </h2>
                    <p className="flex-1 text-sm leading-relaxed text-gray-600">
                      {c.blurb}
                    </p>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700">
                      {idx.cardCta}
                      <span aria-hidden>{isArabic ? "←" : "→"}</span>
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </main>

      <Footer lang={lang} />
    </div>
  );
}
